import { DeleteOutlined } from "@ant-design/icons";
import { Button, Flex, Upload } from "antd";
import type { RcFile } from "antd/es/upload";
import { DocumentFileRow } from "@entities/journal";
import { useIsUnifiedMode } from "@shared/utils";

type OtherDocumentMeta = {
  fileMetadata?: {
    fileId?: string;
    id?: string;
    fileName?: string;
  };
};

type FormFileSelectorFourProps = {
  files: RcFile[];
  filesMeta?: OtherDocumentMeta[];
  onFilesChange: (files: RcFile[]) => void;
  isInfo?: boolean;
};

const archiveMimeTypes = ["application/zip"];

const accept = ["application/pdf", ...archiveMimeTypes];

function getUploadableFile(file: RcFile): File | null {
  if (file instanceof File && file.size > 0) {
    return file;
  }

  const originFile = (file as RcFile & { originFileObj?: File }).originFileObj;
  if (originFile instanceof File && originFile.size > 0) {
    return originFile;
  }

  return null;
}

export function FormFileSelectorFour({
  files,
  filesMeta = [],
  onFilesChange,
  isInfo,
}: FormFileSelectorFourProps) {
  const isUnifiedMode = useIsUnifiedMode();

  const handleFileSelect = ({ fileList }: { fileList: any[] }) => {
    const picked: RcFile[] = fileList
      .map((file) => file.originFileObj ?? file)
      .filter(Boolean);

    const merged = [...files];
    for (const file of picked) {
      const exists = merged.some(
        (existing) =>
          existing.name === file.name &&
          existing.size === file.size &&
          existing.lastModified === file.lastModified,
      );
      if (!exists) {
        merged.push(file);
      }
    }

    onFilesChange(merged);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);

    onFilesChange(updatedFiles);
  };

  return (
    <Flex vertical gap={8}>
      {files.map((file, index) => {
        const meta = filesMeta[index]?.fileMetadata;
        const fileId = meta?.fileId || meta?.id;
        const uploadableFile = fileId ? null : getUploadableFile(file);

        return (
          <Flex
            key={file.uid || `${file.name}-${index}`}
            align="center"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <DocumentFileRow
              className="form-file-selector-link-row"
              linkClassName="form-file-selector-link"
              fileName={meta?.fileName || file.name}
              fileId={fileId}
              localFile={uploadableFile}
              disableLinkAction={isUnifiedMode}
            />
            {isInfo ? null : (
              <Button
                icon={<DeleteOutlined />}
                type="text"
                className="form-file-selector-close"
                onClick={() => handleRemoveFile(index)}
              />
            )}
          </Flex>
        );
      })}

      <Upload
        multiple
        maxCount={999}
        name="documents"
        showUploadList={false}
        accept={accept.filter(Boolean).join(",")}
        beforeUpload={() => false}
        onChange={handleFileSelect}
      >
        {isInfo ? null : (
          <Button className="form-file-selector-button">
            Загрузить с компьютера
          </Button>
        )}
      </Upload>
    </Flex>
  );
}
